// js/app.js
// SPA entry point — initialises Firebase Auth, resolves the user profile,
// then boots the client-side router. Auth fires exactly once on cold load
// and again only when the signed-in user changes.

// ─── Error monitoring — must be first ────────────────────────────────────────
import "./lib/sentry.js";

// ─── CSS bundle — imported here so Vite includes all styles in one pass ──────
import "/css/base.css";
import "/css/auth.css";
import "/css/dashboard.css";
import "/css/history.css";
import "/css/qr.css";
import "/css/scan.css";
import "/css/rewards.css";
import "/css/owner.css";
import "/css/analytics.css";
import "/css/dialog.css";
import "/css/landing.css";
import "/css/messages.css";

import { onAuthChange, signOut } from "./lib/auth.js";
import {
  getUser,
  getShopByOwner,
  getShopBySlug,
  updateUser,
  onUnreadNotificationsCount,
} from "./lib/firestore.js";
import { initUI } from "./lib/ui.js";
import { registerSW } from "./lib/push.js";
import { applyShopPWABranding } from "./lib/install.js";
import { cacheClearAll, cacheGet, cacheSet } from "./lib/cache.js";
import { appState } from "./state.js";
import { navigate, startRouter, setNavigateCallback } from "./router.js";
import { renderCustomerNav } from "./components/customerNav.js";
import { renderOwnerNav } from "./components/ownerNav.js";
import { openQrModal } from "./components/qrModal.js";
import { t, initLocale, setLocale } from "./lib/i18n.js";
import { initTheme, initOwnerTheme, resetTheme } from "./lib/theme.js";
import { getActiveSubdomainSlug } from "./marketing/config.js";
import { captureAndTrackUtm } from "./lib/utmTracker.js";

// ─── UTM tracking — capture on first load before router starts ────────────────
captureAndTrackUtm();

// ─── DOM refs ─────────────────────────────────────────────────────────────────

const loadingEl = document.getElementById("app-loading");
const navEl = document.getElementById("app-nav");
// ─── Unread notifications listener ────────────────────────────────
let _unreadUnsub = null;
// ─── Preview mode ─────────────────────────────────────────────────────────────
// When the app is loaded inside the setup.html phone preview iframe (?preview=1)
// we hide the nav and the loading overlay.
const IS_PREVIEW =
  new URLSearchParams(window.location.search).get("preview") === "1";
if (IS_PREVIEW) document.body.classList.add("preview-mode");

function hideLoading() {
  if (!loadingEl) return;
  loadingEl.classList.add("app-loading--hidden");
  loadingEl.addEventListener("transitionend", () => loadingEl.remove(), {
    once: true,
  });
  // Fallback in case transitionend never fires
  setTimeout(() => loadingEl.remove(), 400);
}

// ─── Service worker — register immediately at boot for PWA installability ────
// Independent of auth or push permission. The SW must exist before Chrome
// can show or defer the beforeinstallprompt event.
registerSW();

// ─── Per-shop PWA branding — apply logo + theme colour early ─────────────────
// If running on a shop subdomain (e.g. barbershop.rewardlix.com):
//  1. Point <link rel="manifest"> to /api/manifest.json?slug=... so the Cloud
//     Function knows which shop to serve (it cannot read the hostname).
//  2. Load the shop from Firestore and update <link rel="apple-touch-icon">
//     and <meta name="theme-color"> for iOS.
// Fire-and-forget: does not block app boot.
(async () => {
  try {
    const slug = getActiveSubdomainSlug();
    if (!slug) return;

    // Update manifest href with slug query param before browser fetches it.
    const manifestEl = document.querySelector("link[rel='manifest']");
    if (manifestEl) {
      const isOwner = window.location.pathname.startsWith("/owner");
      let newHref = `/api/manifest.json?slug=${encodeURIComponent(slug)}`;
      if (isOwner) newHref += "&type=owner";
      manifestEl.href = newHref;
    }

    const shop = await getShopBySlug(slug);
    if (shop) applyShopPWABranding(shop);
  } catch (_) {
    // Non-critical — fallback branding remains
  }
})();

// ─── i18n init — start loading locale immediately so t() is ready before views render ──
const localeReady = initLocale();

// ─── Theme init — reset any stale data-theme before first render ─────────────
initTheme();

// ─── UI init ──────────────────────────────────────────────────────────────────

initUI();

// ─── Nav management ───────────────────────────────────────────────────────────

let currentNavType = null; // 'customer' | 'owner' | null
let navInstance = null;

// ─── Owner preview banner ─────────────────────────────────────────────────────

let previewBannerEl = null;

// Also show the banner when dashboardView signals it is ready (first load)
document.addEventListener("ownerPreviewStart", () => showPreviewBanner());

function showPreviewBanner() {
  if (previewBannerEl) return; // already shown
  previewBannerEl = document.createElement("div");
  previewBannerEl.className = "owner-preview-banner";
  previewBannerEl.innerHTML = `
    <span class="owner-preview-banner__text">${t("ownerPreview.bannerText")}</span>
    <button class="owner-preview-banner__exit" type="button" id="owner-preview-exit">
      ${t("ownerPreview.bannerExit")}
    </button>
  `;
  document.body.appendChild(previewBannerEl);
  previewBannerEl
    .querySelector("#owner-preview-exit")
    ?.addEventListener("click", () => {
      appState.ownerPreview = false;
      sessionStorage.removeItem("ownerPreview");
      hidePreviewBanner();
      navigate("/owner", { replace: true });
    });
}

function hidePreviewBanner() {
  if (previewBannerEl && previewBannerEl.parentNode) {
    previewBannerEl.parentNode.removeChild(previewBannerEl);
  }
  previewBannerEl = null;
}

setNavigateCallback((route, path) => {
  const guard = route.guard;

  if (IS_PREVIEW) {
    // In preview: render the nav for visual decoration but all clicks are
    // disabled via CSS (pointer-events: none on body.preview-mode #app-nav)
    if (guard === "owner" && currentNavType !== "owner") {
      currentNavType = "owner";
      navEl.innerHTML = "";
      navInstance = renderOwnerNav(navEl, { navigate: () => {} });
      navInstance.setUserInfo(appState.profile);
    } else if (guard === "customer" && currentNavType !== "customer") {
      currentNavType = "customer";
      navEl.innerHTML = "";
      navInstance = renderCustomerNav(navEl, {
        openQrModal: () => {},
        navigate: () => {},
      });
    }
    navInstance?.setActive(path);
    navEl.hidden = !(guard === "owner" || guard === "customer");
    return;
  }

  if (guard === "customer") {
    if (currentNavType !== "customer") {
      currentNavType = "customer";
      navEl.innerHTML = "";
      navInstance = renderCustomerNav(navEl, { openQrModal, navigate });
      // Subscribe to unread notification count for the current shop only.
      // Re-resolve the shopId each time we enter the customer nav so that
      // switching between shops (on localhost with ?slug= param) is correct.
      _unreadUnsub?.();
      if (appState.user?.uid) {
        const slug =
          getActiveSubdomainSlug() ||
          appState.shopSlug ||
          sessionStorage.getItem("shopSlug") ||
          null;
        const resolveShopId = async () => {
          if (!slug) return null;
          const cached = cacheGet(`shop:slug:${slug}`);
          if (cached?.id) return cached.id;
          try {
            const shop = await getShopBySlug(slug);
            return shop?.id ?? null;
          } catch (_) {
            return null;
          }
        };
        resolveShopId().then((shopId) => {
          _unreadUnsub?.();
          if (!shopId || !appState.user?.uid) return;
          _unreadUnsub = onUnreadNotificationsCount(
            appState.user.uid,
            shopId,
            (count) => {
              navInstance?.setUnreadCount?.(count);
            },
          );
        });
      }
    }
    navInstance?.setActive(path);
    navEl.hidden = false;
    // Show the owner-preview banner if the owner is in preview mode
    if (appState.ownerPreview) showPreviewBanner();
  } else if (guard === "owner") {
    // Leaving customer routes — remove preview banner if present
    hidePreviewBanner();
    // Unsubscribe unread count listener when leaving customer area
    _unreadUnsub?.();
    _unreadUnsub = null;
    if (currentNavType !== "owner") {
      currentNavType = "owner";
      navEl.innerHTML = "";
      navInstance = renderOwnerNav(navEl, { navigate });
      navInstance.setUserInfo(appState.profile);
    }
    navInstance?.setActive(path);
    navEl.hidden = false;
  } else {
    // Public pages — hide the nav and banner
    hidePreviewBanner();
    _unreadUnsub?.();
    _unreadUnsub = null;
    navEl.hidden = true;
    if (currentNavType !== null) {
      navEl.innerHTML = "";
      currentNavType = null;
      navInstance = null;
    }
  }
});

// ─── Auth boot ────────────────────────────────────────────────────────────────

let routerStarted = false;

onAuthChange(async (firebaseUser) => {
  if (!firebaseUser) {
    appState.user = null;
    appState.profile = null;
    appState.shopSlug = null;
    sessionStorage.removeItem("shopSlug");
    cacheClearAll();

    // Ensure locale is loaded before first render
    await localeReady;

    hideLoading();

    if (!routerStarted) {
      routerStarted = true;
      startRouter();
    } else {
      // User signed out mid-session
      // Exception: /impersonate signs out before re-signing in — don't interrupt it
      if (window.location.pathname === "/impersonate") return;
      const redirect = localStorage.getItem("logoutRedirect");
      if (redirect) {
        localStorage.removeItem("logoutRedirect");
        navigate(redirect, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
    return;
  }

  // User is signed in — load Firestore profile
  // Exception: /impersonate manages its own sign-in flow and navigates away
  // via window.location.replace. Any profile loading or error handling here
  // would race with the view and could call signOut() before the redirect.
  // However, if the router hasn't started yet (e.g. user already had a session
  // in this browser when the tab opened), we must still boot the router so the
  // view can mount — then return.
  if (window.location.pathname === "/impersonate") {
    if (!routerStarted) {
      hideLoading();
      routerStarted = true;
      startRouter();
    }
    return;
  }

  // Force-refresh the ID token first to ensure Firestore rules see the latest
  // auth credential. This prevents a race condition after a fresh Google OAuth
  // sign-in where the token hasn't yet propagated to Firestore's rule engine.
  let profile;
  try {
    try {
      await firebaseUser.getIdToken(/* forceRefresh */ true);
    } catch (_) {
      // Token refresh is best-effort; continue with existing token if it fails
    }
    profile = await getUser(firebaseUser.uid);
  } catch (err) {
    // One retry after a short delay to handle persistent cache / auth sync lag
    console.warn("[app] First profile read failed, retrying in 1 s…", err.code);
    try {
      await new Promise((r) => setTimeout(r, 1000));
      profile = await getUser(firebaseUser.uid);
    } catch (err2) {
      console.error("[app] Failed to load profile:", err2);
      await signOut();
      navigate("/login", { replace: true });
      return;
    }
  }

  if (!profile) {
    console.warn(
      "[app] No Firestore profile for uid:",
      firebaseUser.uid,
      "reattempting or creating default profile.",
    );
    try {
      // If profile really doesn't exist, we fallback to creating a basic customer profile
      // This handles the race condition where Google signup triggers onAuthChange before getOrCreateUser completes.
      const { getOrCreateUser } = await import("./lib/firestore.js");
      profile = await getOrCreateUser(firebaseUser, "customer");
    } catch (createErr) {
      console.error(
        "[app] Final fallback failed to create profile:",
        createErr,
      );
      await signOut();
      hideLoading();
      navigate("/login", { replace: true });
      return;
    }
  }

  appState.user = firebaseUser;
  appState.profile = profile;

  // If the user just completed a payment, poll Firestore until the webhook
  // updates the subscription plan (up to 20 s), then render with the correct plan.
  if (
    profile.role === "owner" &&
    profile.subscriptionPlan !== "premium" &&
    localStorage.getItem("rewardlix.paymentPending") === "1"
  ) {
    localStorage.removeItem("rewardlix.paymentPending");
    console.log(
      "[app] Payment detected — waiting for subscription to activate…",
    );
    const POLL_INTERVAL_MS = 2000;
    const POLL_MAX_ATTEMPTS = 10; // 10 × 2 s = 20 s
    let attempts = 0;
    while (attempts < POLL_MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      attempts++;
      try {
        const fresh = await getUser(firebaseUser.uid);
        if (
          fresh?.subscriptionPlan === "premium" ||
          fresh?.recurringPlan === "premium"
        ) {
          console.log(
            "[app] Subscription activated after",
            attempts * POLL_INTERVAL_MS,
            "ms",
          );
          profile = fresh;
          appState.profile = fresh;
          break;
        }
      } catch (pollErr) {
        console.warn("[app] Poll attempt", attempts, "failed:", pollErr);
      }
    }
  }

  // Run independent post-profile tasks in parallel:
  // 1. Sync photoURL (fire-and-forget write)
  // 2. Load owner shop (if owner)
  // 3. Wait for locale
  const parallelTasks = [];

  // Sync/Backfill photoURL if missing or changed in Google/Firebase Auth
  if (firebaseUser.photoURL && profile.photoURL !== firebaseUser.photoURL) {
    parallelTasks.push(
      updateUser(firebaseUser.uid, { photoURL: firebaseUser.photoURL })
        .then(() => {
          appState.profile.photoURL = firebaseUser.photoURL;
          cacheSet(
            `user:${firebaseUser.uid}`,
            { ...profile, photoURL: firebaseUser.photoURL },
            24 * 60 * 60 * 1000,
          );
        })
        .catch((e) => console.warn("[app] Failed to sync photoURL:", e)),
    );
  }

  // Apply stored customer theme preference (defaults gold-light if none stored)
  if (profile.role === "customer") {
    resetTheme();
  }

  // Load shop if owner
  if (profile.role === "owner") {
    parallelTasks.push(
      (async () => {
        try {
          let shop = cacheGet(`ownerShop:${firebaseUser.uid}`);
          if (!shop) {
            shop = await getShopByOwner(firebaseUser.uid);
            if (shop)
              cacheSet(`ownerShop:${firebaseUser.uid}`, shop, 30 * 60 * 1000);
          }
          appState.shop = shop;
          // Apply the owner's personal app theme preference (localStorage, defaults gold-light)
          initOwnerTheme();
          if (shop) {
            applyShopPWABranding(shop);
            const manifestEl = document.querySelector("link[rel='manifest']");
            if (manifestEl && shop.slug) {
              manifestEl.href = `/api/manifest.json?slug=${encodeURIComponent(shop.slug)}&type=owner`;
            }
          }
        } catch (err) {
          console.error("[app] Failed to load owner shop:", err);
        }
      })(),
    );
  }

  // Ensure locale is loaded, then sync the user's saved locale preference
  parallelTasks.push(
    localeReady.then(async () => {
      if (profile.locale) {
        await setLocale(profile.locale, {
          persist: true,
          syncFirestore: false,
        });
      }
    }),
  );

  await Promise.all(parallelTasks);

  hideLoading();

  if (!routerStarted) {
    routerStarted = true;
    startRouter();
    // Push notification init is handled by dashboardView after confirming the
    // membership document exists. Calling initPush here (before membership is
    // created) would race with the ensure_shop_membership CF and cause a
    // Firestore permissions error on the first write attempt.
  } else {
    // Auth user changed mid-session (e.g. different account) — reload cleanly
    // Exception: /impersonate manages its own post-sign-in navigation
    if (window.location.pathname === "/impersonate") return;
    window.location.reload();
  }
});

// ─── Pull-to-refresh (iOS standalone PWA only) ────────────────────────────────
// Android handles this natively; we only need to replicate it on iOS PWA.
if (navigator.standalone === true) {
  const THRESHOLD = 80; // px of pull needed to trigger reload
  let startY = 0;
  let pulling = false;
  let indicator = null;

  const getIndicator = () => {
    if (!indicator) {
      indicator = document.createElement("div");
      indicator.id = "ptr-indicator";
      indicator.style.cssText = [
        "position:fixed",
        "top:0",
        "left:0",
        "right:0",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "height:0",
        "overflow:hidden",
        "transition:height 0.15s ease",
        "background:var(--color-surface)",
        "z-index:9999",
        "pointer-events:none",
      ].join(";");
      indicator.innerHTML =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>';
      document.body.prepend(indicator);
    }
    return indicator;
  };

  document.addEventListener(
    "touchstart",
    (e) => {
      if (window.scrollY === 0 && e.touches.length === 1) {
        startY = e.touches[0].clientY;
        pulling = true;
      }
    },
    { passive: true },
  );

  document.addEventListener(
    "touchmove",
    (e) => {
      if (!pulling) return;
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        pulling = false;
        getIndicator().style.height = "0";
        return;
      }
      const h = Math.min(delta * 0.4, THRESHOLD * 0.6);
      getIndicator().style.height = h + "px";
    },
    { passive: true },
  );

  document.addEventListener("touchend", (e) => {
    if (!pulling) return;
    pulling = false;
    const delta = e.changedTouches[0].clientY - startY;
    getIndicator().style.height = "0";
    if (delta >= THRESHOLD) {
      window.location.reload();
    }
  });
}
